import React, { memo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  getInputIcon,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import * as Types from 'apps/fleet-planner/types'
import { fetchCustomers } from 'apps/fleet-planner/utils/actions/customers'
import { customerStateSelector } from 'apps/fleet-planner/utils/selectors/customer'
import { getEntityById } from 'utils/getEntityById'

export const NEW_CUSTOMER_ID = '-1'

interface ICustomerAutosuggestProps extends IAutosuggestWrapperProps<Types.ICustomer> {}

export const CustomerAutosuggest = memo<ICustomerAutosuggestProps>(
  ({
    disabled: disabledProp,
    error: errorProp,
    fetchList = true,
    large: largeProp = true,
    onAddEntity: onAddEntityProp,
    onBlur: onBlurProp,
    onCancel: onCancelProp,
    onChange: onChangeProp,
    onInputChange: onInputChangeProp,
    placeholder: placerholderProp = 'Search by customer name',
    value,
  }) => {
    const { isMobile } = useBreakpointContext()
    const dispatch = useDispatch()
    const { customers = [] } = useSelector(customerStateSelector)

    const [inputValue, setInputValue] = useState('')

    /**
     * If true, fetch on mount, but only run once
     */
    useEffect(() => {
      if (fetchList && !customers.length) dispatch(fetchCustomers({ pageSize: 500 }))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const changeHandler = (option: IAutocompleteOption | undefined) => {
      const newCustomer = getEntityById(customers, option?.id)
      onChangeProp(newCustomer)
    }

    const options = getAutosuggestOptions({
      options: customers,
      groupIndex: 1,
      entityName: 'New Customer',
      groupName: 'Customers',
      iconName: 'CustomerIcon',
      inputValue,
      mapOption: ({ id = '', importedFromQuickbooks = false, name: text }) => ({
        id,
        text,
        description: importedFromQuickbooks ? '(QuickBooks Customer)' : '',
      }),
    })

    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
      if (onInputChangeProp) onInputChangeProp(val)
    }

    const icon = getInputIcon('search', inputValue)

    return (
      <Autosuggest
        id='customer'
        label='Customer'
        placeholder={placerholderProp}
        dataTest='customer-autosuggest'
        options={options}
        value={value}
        icon={icon}
        renderOptionType='withIconAndDescription'
        onChange={changeHandler}
        onAddEntity={onAddEntityProp}
        onCancel={onCancelProp}
        onInputChange={inputChangeHandler}
        clearable
        error={errorProp}
        onBlur={onBlurProp}
        disabled={disabledProp}
        selectOnFocus={!isMobile}
        large={largeProp}
        filterOptionsType='fuzzyGroup'
      />
    )
  }
)
